<template>
  <form class="contact-form" @submit.prevent="handleSubmit">
    <div class="contact-form__field">
      <label class="contact-form__label" for="contact-name">
        {{ t('about.contact.form.nameLabel') }}
      </label>
      <input
        id="contact-name"
        v-model="form.name"
        class="contact-form__input"
        type="text"
        :placeholder="t('about.contact.form.namePlaceholder')"
        required
        autocomplete="name"
      />
    </div>

    <div class="contact-form__field">
      <label class="contact-form__label" for="contact-email">
        {{ t('about.contact.form.emailLabel') }}
      </label>
      <input
        id="contact-email"
        v-model="form.email"
        class="contact-form__input"
        type="email"
        :placeholder="t('about.contact.form.emailPlaceholder')"
        required
        autocomplete="email"
      />
    </div>

    <div class="contact-form__field">
      <label class="contact-form__label" for="contact-subject">
        {{ t('about.contact.form.subjectLabel') }}
      </label>
      <input
        id="contact-subject"
        v-model="form.subject"
        class="contact-form__input"
        type="text"
        :placeholder="t('about.contact.form.subjectPlaceholder')"
        autocomplete="off"
      />
    </div>

    <div class="contact-form__field">
      <label class="contact-form__label" for="contact-message">
        {{ t('about.contact.form.messageLabel') }}
      </label>
      <textarea
        id="contact-message"
        v-model="form.message"
        class="contact-form__textarea"
        rows="5"
        :placeholder="t('about.contact.form.messagePlaceholder')"
        required
      />
    </div>

    <div class="contact-form__consent-field">
      <label class="contact-form__consent-label">
        <input
          v-model="form.consent"
          class="contact-form__consent-checkbox"
          type="checkbox"
          :aria-required="true"
        />
        <span class="contact-form__consent-text">{{ t('contact.consent.checkbox') }}</span>
      </label>
      <p
        v-if="consentError"
        role="alert"
        class="contact-form__consent-error"
      >{{ t('contact.consent.errorRequired') }}</p>
    </div>

    <div class="contact-form__footer">
      <KdsButton
        variant="primary"
        size="md"
        type="submit"
        :disabled="!form.consent"
        :loading="loading"
        :aria-busy="loading ? 'true' : undefined"
        class="contact-form__submit"
      >
        {{ t('about.contact.form.submit') }}
      </KdsButton>
    </div>

    <div
      role="status"
      aria-live="polite"
      class="contact-form__feedback"
      :class="{
        'contact-form__feedback--success': feedbackType === 'success',
        'contact-form__feedback--error': feedbackType === 'error',
        'contact-form__feedback--visible': Boolean(feedbackMessage)
      }"
    >
      <span v-if="feedbackMessage" class="contact-form__feedback-text">
        {{ feedbackMessage }}
      </span>
    </div>
  </form>
</template>

<script setup lang="ts">
import { ref, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import KdsButton from '@/kds/primitives/KdsButton.vue';
import { trackEvent } from '@/boot/analytics';

const { t } = useI18n();

const loading = ref(false);
const feedbackMessage = ref('');
const feedbackType = ref<'success' | 'error' | ''>('');
const consentError = ref(false);

const form = reactive({
  name: '',
  email: '',
  subject: '',
  message: '',
  consent: false
});

const resetForm = (): void => {
  form.name = '';
  form.email = '';
  form.subject = '';
  form.message = '';
  form.consent = false;
};

const handleSubmit = async (): Promise<void> => {
  if (!form.consent) {
    consentError.value = true;
    return;
  }
  consentError.value = false;
  loading.value = true;
  feedbackMessage.value = '';
  feedbackType.value = '';

  try {
    const res = await fetch('/api/contact', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: form.name,
        email: form.email,
        subject: form.subject,
        message: form.message,
        consent: form.consent
      })
    });

    const data = (await res.json()) as { ok: boolean };

    if (data.ok) {
      feedbackType.value = 'success';
      feedbackMessage.value = t('about.contact.form.success');
      trackEvent('contact_form_submit');
      resetForm();
    } else {
      feedbackType.value = 'error';
      feedbackMessage.value = t('about.contact.form.error');
    }
  } catch {
    feedbackType.value = 'error';
    feedbackMessage.value = t('about.contact.form.error');
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped lang="scss">
.contact-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);

  &__field {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  &__label {
    font-size: var(--fs-body-sm);
    font-weight: var(--fw-medium);
    color: var(--ink);
  }

  &__input,
  &__textarea {
    height: 40px;
    padding: 10px var(--space-4);
    border: var(--border);
    border-radius: var(--r-3);
    font-family: var(--font-ui);
    font-size: var(--fs-body);
    color: var(--ink);
    background: var(--paper);
    transition: border-color var(--d-fast) var(--ease-out), box-shadow var(--d-fast) var(--ease-out);
    width: 100%;
    box-sizing: border-box;

    &::placeholder {
      color: var(--stone-2);
    }

    &:focus {
      outline: none;
      border-color: var(--ink);
      box-shadow: var(--shadow-focus);
    }
  }

  &__textarea {
    height: auto;
    resize: vertical;
    min-height: 120px;
  }

  &__consent-field {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  &__consent-label {
    display: flex;
    align-items: flex-start;
    gap: var(--space-2);
    cursor: pointer;
  }

  &__consent-checkbox {
    margin-top: 3px;
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    accent-color: var(--ink);
    cursor: pointer;
  }

  &__consent-text {
    font-size: var(--fs-body-sm);
    line-height: 1.55;
    color: var(--stone);
  }

  &__consent-error {
    margin: 0;
    font-size: var(--fs-body-sm);
    color: var(--danger);
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
  }

  &__submit {
    @media (max-width: 639px) {
      width: 100%;
    }
  }

  &__feedback {
    min-height: 24px;
    font-size: var(--fs-body-sm);
    opacity: 0;
    transition: opacity var(--d-base) var(--ease-out);

    &--visible {
      opacity: 1;
    }

    &--success {
      color: var(--ok);
    }

    &--error {
      color: var(--danger);
    }
  }
}
</style>
